import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Users, Activity, Bell, ChevronRight, TrendingUp, AlertTriangle,
  CreditCard, Search, UserCircle, ShoppingCart, Mail, Inbox,
} from 'lucide-react';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [demoLeads, setDemoLeads] = useState([]);
  const [demoLeadsLoading, setDemoLeadsLoading] = useState(true);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientsLoading, setClientsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/dashboard').then((r) => setStats(r.data));
    api.get('/admin/reports/arrivals-departures').then((r) => {
      setArrivals(r.data.arrivals);
      setDepartures(r.data.departures);
    });
    // Newest demo requests widget — pulls the 5 most recent leads so
    // Greg can jump on them straight from the admin home without
    // clicking through to a dedicated inbox screen.
    api.get('/admin/demo-requests', { params: { limit: 5 } })
      .then((r) => setDemoLeads(r.data || []))
      .catch(() => setDemoLeads([]))
      .finally(() => setDemoLeadsLoading(false));
  }, []);

  const openClientPicker = async () => {
    setClientPickerOpen(true);
    if (allClients.length === 0) {
      setClientsLoading(true);
      try {
        const { data } = await api.get('/admin/clients');
        setAllClients(data);
      } finally { setClientsLoading(false); }
    }
  };

  const filtered = allClients.filter((c) => {
    if (!clientSearch.trim()) return true;
    const q = clientSearch.trim().toLowerCase();
    return (
      c.full_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase?.()?.includes(q)
    );
  });

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto'>
      <div className='flex items-center justify-between mb-6 flex-wrap gap-3'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Operations</div>
          <h1 className='font-display text-2xl sm:text-3xl font-bold mt-1 text-slate-900 dark:text-slate-50'>
            Day at a glance
          </h1>
          <p className='text-slate-500 dark:text-slate-400 text-sm mt-1'>
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <Button
            onClick={openClientPicker}
            className='bg-[#C8102E] hover:bg-[#A50D26] text-white shadow-md'
            data-testid='dashboard-charge-client-button'
          >
            <CreditCard className='w-4 h-4 mr-2' /> Charge Client
          </Button>
          <Button
            onClick={() => navigate('/admin/pos')}
            className='bg-[#1B3A87] hover:bg-[#16306F] text-white shadow-md font-semibold'
            data-testid='dashboard-pos-button'
          >
            <ShoppingCart className='w-4 h-4 mr-2' /> Open POS
          </Button>
        </div>
      </div>

      <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        {[
          { l: 'Arrivals today', v: stats?.arrivals_today ?? '—', icon: Activity, color: '#167A5A', tid: 'kpi-arrivals' },
          { l: 'Departures today', v: stats?.departures_today ?? '—', icon: Activity, color: '#B45309', tid: 'kpi-departures' },
          { l: 'Occupancy', v: stats ? `${stats.occupancy_pct}%` : '—', icon: TrendingUp, color: '#1B3A87', tid: 'kpi-occupancy', sub: stats ? `${stats.occupied_runs}/${stats.total_runs} runs` : '' },
          { l: 'Unpaid bookings', v: stats?.unpaid_bookings ?? '—', icon: Bell, color: '#C8102E', tid: 'kpi-unpaid' },
        ].map((k) => (
          <Card
            key={k.l}
            className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'
            data-testid={k.tid}
          >
            <div className='flex items-center justify-between'>
              <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400'>{k.l}</div>
              <k.icon className='w-4 h-4' style={{ color: k.color }} />
            </div>
            <div className='font-display text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-50 mt-2'>{k.v}</div>
            {k.sub && <div className='text-xs text-slate-500 dark:text-slate-400 mt-1'>{k.sub}</div>}
          </Card>
        ))}
      </div>

      <div className='grid lg:grid-cols-3 gap-4 mt-6'>
        <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 lg:col-span-2'>
          <div className='flex items-center justify-between mb-3'>
            <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50'>Arrivals · Today</h3>
            <Button
              size='sm'
              variant='outline'
              className='border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
              onClick={() => navigate('/admin/bookings')}
              data-testid='admin-view-bookings'
            >
              All bookings <ChevronRight className='w-4 h-4 ml-1' />
            </Button>
          </div>
          {arrivals.length === 0 ? (
            <div className='text-slate-500 dark:text-slate-400 text-sm py-4'>No arrivals scheduled today.</div>
          ) : (
            <div className='space-y-2'>
              {arrivals.map((a) => (
                <button
                  key={a.id}
                  className='w-full flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-[#1B3A87] dark:hover:border-[#93C5FD] text-left transition-colors'
                  onClick={() => navigate(`/admin/clients/${a.client_id}`)}
                  data-testid={`arrival-${a.id}`}
                >
                  <div>
                    <div className='font-semibold text-sm text-slate-900 dark:text-slate-50'>
                      {a.dog_name} · <span className='font-mono text-xs'>{a.run_name}</span>
                    </div>
                    <div className='text-xs text-slate-500 dark:text-slate-400'>
                      {a.client_name} · {new Date(a.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <Badge className='bg-[#167A5A]/15 text-[#167A5A] dark:text-[#7DD3A8]'>{a.status.replace('_', ' ')}</Badge>
                </button>
              ))}
            </div>
          )}
        </Card>
        <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
          <div className='flex items-center justify-between mb-3'>
            <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50'>Departures</h3>
          </div>
          {departures.length === 0 ? (
            <div className='text-slate-500 dark:text-slate-400 text-sm py-4'>No departures today.</div>
          ) : (
            <div className='space-y-2'>
              {departures.map((a) => (
                <button
                  key={a.id}
                  className='w-full p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-[#1B3A87] dark:hover:border-[#93C5FD] text-left transition-colors'
                  onClick={() => navigate(`/admin/clients/${a.client_id}`)}
                  data-testid={`departure-${a.id}`}
                >
                  <div className='font-semibold text-sm text-slate-900 dark:text-slate-50'>{a.dog_name}</div>
                  <div className='text-xs text-slate-500 dark:text-slate-400'>{a.client_name}</div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* ============= Lead Inbox — newest demo requests =============
          Surfaces the last 5 demo requests on the admin home so hot
          leads never sit unread. Click a row to open the full inbox. */}
      <Card
        className='mt-6 p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'
        data-testid='lead-inbox-widget'
      >
        <div className='flex items-center justify-between mb-3 flex-wrap gap-2'>
          <div className='flex items-center gap-2'>
            <div className='w-9 h-9 rounded-lg bg-[#C8102E]/10 flex items-center justify-center'>
              <Inbox className='w-4 h-4 text-[#C8102E]' />
            </div>
            <div>
              <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50 leading-tight'>
                Latest demo requests
              </h3>
              <p className='text-xs text-slate-500 dark:text-slate-400'>Hottest leads from your site &middot; last 5</p>
            </div>
          </div>
          <Button
            size='sm'
            variant='outline'
            className='border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
            onClick={() => navigate('/admin/marketing')}
            data-testid='lead-inbox-view-all'
          >
            View all <ChevronRight className='w-4 h-4 ml-1' />
          </Button>
        </div>
        {demoLeadsLoading ? (
          <div className='py-6 text-sm text-slate-500'>Loading leads…</div>
        ) : demoLeads.length === 0 ? (
          <div className='py-8 text-center text-sm text-slate-500 dark:text-slate-400'>
            No demo requests yet &mdash; leads will appear here the moment they submit.
          </div>
        ) : (
          <div className='space-y-2'>
            {demoLeads.map((lead) => (
              <div
                key={lead.id}
                className='flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-[#C8102E]/50 transition-colors'
                data-testid={`lead-inbox-row-${lead.id}`}
              >
                <div className='w-9 h-9 rounded-full bg-[#1B3A87] text-white flex items-center justify-center font-semibold text-sm shrink-0'>
                  {lead.full_name?.[0]?.toUpperCase() || <Mail className='w-4 h-4' />}
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <div className='font-semibold text-sm text-slate-900 dark:text-slate-50 truncate'>
                      {lead.full_name}
                    </div>
                    {lead.status && (
                      <Badge
                        className={`text-[10px] ${
                          lead.status === 'NEW'
                            ? 'bg-[#C8102E]/15 text-[#C8102E] hover:bg-[#C8102E]/15'
                            : lead.status === 'CONTACTED'
                              ? 'bg-[#B45309]/15 text-[#B45309] hover:bg-[#B45309]/15'
                              : lead.status === 'BOOKED'
                                ? 'bg-[#167A5A]/15 text-[#167A5A] hover:bg-[#167A5A]/15'
                                : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {lead.status}
                      </Badge>
                    )}
                  </div>
                  <div className='text-xs text-slate-500 dark:text-slate-400 truncate'>
                    <a
                      href={`mailto:${lead.email}`}
                      className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD]'
                      onClick={(e) => e.stopPropagation()}
                    >
                      {lead.email}
                    </a>
                    {lead.location ? ` · ${lead.location}` : ''}
                    {lead.facility_name ? ` · ${lead.facility_name}` : ''}
                  </div>
                  {lead.notes && (
                    <div className='text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2'>
                      &ldquo;{lead.notes}&rdquo;
                    </div>
                  )}
                </div>
                <div className='text-[10px] text-slate-400 tabular-nums shrink-0'>
                  {formatLeadRelative(lead.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {stats?.expiring_vaccinations > 0 && (
        <Card className='p-4 bg-[#B45309]/10 border-[#B45309]/30 mt-4 flex items-center gap-3'>
          <AlertTriangle className='w-5 h-5 text-[#FBBF24]' />
          <div className='flex-1'>
            <div className='font-semibold text-sm text-slate-900 dark:text-slate-50'>
              {stats.expiring_vaccinations} vaccinations expiring in next 30 days
            </div>
            <div className='text-xs text-slate-600 dark:text-slate-300'>Reach out to clients before their bookings.</div>
          </div>
          <Button
            size='sm'
            variant='outline'
            className='border-[#B45309]/40 bg-transparent text-slate-900 dark:text-slate-100'
            onClick={() => navigate('/admin/reports')}
            data-testid='admin-vax-alert-button'
          >
            View report
          </Button>
        </Card>
      )}

      {/* ============= Charge Client modal ============= */}
      <Dialog open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
        <DialogContent className='max-w-lg' data-testid='charge-client-dialog'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <CreditCard className='w-5 h-5 text-[#C8102E]' /> Select a client to charge
            </DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='relative'>
              <Search className='w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
              <Input
                placeholder='Search by name, email, or phone…'
                className='pl-9'
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                autoFocus
                data-testid='charge-client-search'
              />
            </div>
            <div className='max-h-[55vh] overflow-y-auto -mx-1 px-1'>
              {clientsLoading ? (
                <div className='text-center py-8 text-slate-500 text-sm'>Loading clients…</div>
              ) : filtered.length === 0 ? (
                <div className='text-center py-8 text-slate-500 text-sm'>No clients found.</div>
              ) : (
                <div className='space-y-1.5'>
                  {filtered.slice(0, 40).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setClientPickerOpen(false);
                        navigate(`/admin/pos?client_id=${c.id}`);
                      }}
                      className='w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-[#1B3A87] dark:hover:border-[#93C5FD] hover:shadow-sm text-left transition-all'
                      data-testid={`charge-client-row-${c.id}`}
                    >
                      <div className='w-9 h-9 rounded-full bg-[#1B3A87] text-white flex items-center justify-center font-semibold text-sm shrink-0'>
                        {c.full_name?.[0]?.toUpperCase() || <UserCircle className='w-4 h-4' />}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='text-sm font-semibold text-slate-900 dark:text-slate-50 truncate'>{c.full_name}</div>
                        <div className='text-xs text-slate-500 dark:text-slate-400 truncate'>{c.email} {c.phone ? `· ${c.phone}` : ''}</div>
                      </div>
                      <ChevronRight className='w-4 h-4 text-slate-400 shrink-0' />
                    </button>
                  ))}
                  {filtered.length > 40 && (
                    <div className='text-center py-2 text-xs text-slate-400'>
                      Showing 40 of {filtered.length} — refine your search.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Relative-time formatter for the Lead Inbox rows. */
const formatLeadRelative = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h`;
  return `${Math.round(diff / 86_400_000)}d`;
};
